import React from 'react'
import { Box, capitalize } from '@mui/material'
import { formatDate, formatProductsUnits } from '@ui-seeker/utils'
import { IconSeeker } from '@ui-seeker/core'

import { AppDetailList, AppDetailSection, AppStatTile, AppValueChip } from '@components'
import { dsColorNew } from '@palette'
import { DAYS, RENTAL_DATES_OPTIONS } from '../../../../@constants'
import { dateFormatReceiver } from '@utils'

const chips = (values = [], label = value => value) => values?.length
	? values.map((value, idx) => <AppValueChip key={`${value}-${idx}`}>{label(value)}</AppValueChip>)
	: null

const padTime = time => time?.length === 4 ? `0${time}` : time


export default function ReportInfoModalDetails({ data }) {

	const {
		timeFrom, timeTo, rentalDateOption, rentalDateOptionTranslation, lengthOfRental, weekdayFilters, daysMonitorized,
		numSearches, idCurrencyTranslation, notificationEmail, specificDates, weekSelected, withinDays, daysSelected, nextFullMonth
	} = data
	const isSpecificDates = rentalDateOption === RENTAL_DATES_OPTIONS.SPECIFIC_DAYS
	const isLeadTimes = rentalDateOption === RENTAL_DATES_OPTIONS.LEAD_TIMES
	const isDayWeek = rentalDateOption === RENTAL_DATES_OPTIONS.DAY_SELECTED
	const isFullMonth = rentalDateOption === RENTAL_DATES_OPTIONS.FULL_MONTH
	const isDanger = numSearches >= 1000

	// how many search dates the report has, for the date option it uses
	const datesCount = isSpecificDates ? { label: 'Rental dates', value: specificDates?.length }
		: isLeadTimes ? { label: 'Lead times', value: withinDays?.length }
			: isDayWeek ? { label: 'Days', value: daysSelected?.length }
				: isFullMonth ? { label: 'Full months', value: nextFullMonth?.length }
					: null

	return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
		<Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 1.5 }}>
			<AppStatTile
				icon="magnifying-glass"
				label="Searches"
				tone={isDanger ? 'red' : 'green'}
				valueColor={isDanger ? dsColorNew.dangerRed : dsColorNew.successText}
				value={<>
					{!!isDanger && <IconSeeker icon="fire" sx={{ fontSize: 14 }}/>}
					{formatProductsUnits(numSearches)}
				</>}
			/>
			{!!datesCount?.value && <AppStatTile icon="calendar-days" label={datesCount.label} tone="blue" value={datesCount.value}/>}
			{!!daysMonitorized && <AppStatTile icon="hourglass-half" label="Days to monitor" tone="orange" value={formatProductsUnits(daysMonitorized)}/>}
			<AppStatTile icon="coins" label="Currency" tone="purple" value={idCurrencyTranslation || '—'}/>
		</Box>

		<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
			<AppDetailSection icon="calendar" title="Search dates" sx={{ height: '100%', boxSizing: 'border-box' }}>
				<AppDetailList items={[
					{ label: 'Rental date option', value: rentalDateOptionTranslation && capitalize(rentalDateOptionTranslation) },
					isSpecificDates && {
						label: 'Dates of rental',
						value: chips(specificDates && [...specificDates].sort(), date => formatDate(date.split("T")[0], {
							isString: true,
							receivedFormat: dateFormatReceiver(date.split("T")[0])
						})),
					},
					isLeadTimes && { label: 'Within days', value: chips(withinDays, formatProductsUnits) },
					isDayWeek && { label: 'Days', value: chips(daysSelected, day => `${DAYS[day]}`) },
					isDayWeek && { label: 'Weeks', value: chips(weekSelected, formatProductsUnits) },
					isFullMonth && { label: 'Next full months', value: chips(nextFullMonth, formatProductsUnits) },
					{ label: 'Length of rental', value: chips(lengthOfRental) },
					{ label: 'Filter per day of the week', value: chips(weekdayFilters) },
				].filter(Boolean)}/>
			</AppDetailSection>

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				{(!!timeFrom || !!timeTo) && <AppDetailSection icon="clock" title="Times">
					<AppDetailList labelWidth={110} items={[
						{ label: 'Pick-up', value: !!timeFrom && padTime(timeFrom) },
						{ label: 'Drop-off', value: !!timeTo && padTime(timeTo) },
					]}/>
				</AppDetailSection>}

				<AppDetailSection icon="envelope" title="Notifications" sx={{ flex: 1 }}>
					<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
						{notificationEmail?.length
							? notificationEmail.map((email, idx) => <AppValueChip key={idx} tone="accent">{email}</AppValueChip>)
							: <AppValueChip tone="empty">No emails</AppValueChip>}
					</Box>
				</AppDetailSection>
			</Box>
		</Box>
	</Box>
}
